import { useEffect, useRef, type RefObject } from "react";
import { lockBodyScroll } from "../lib/scroll-lock";

const FOCUSABLE =
  'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary,a[href],[tabindex="0"]';

/**
 * Keeps keyboard focus inside a dialog while it is open: focuses it, wraps Tab and Shift+Tab at
 * its ends, calls `onEscape` for an Escape nothing inside has already handled, stops the page
 * behind it scrolling, and hands focus back to whatever had it on close. Only elements actually on
 * screen count as its ends.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  onEscape: () => void,
  active = true,
) {
  const escape = useRef(onEscape);
  escape.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const before = document.activeElement as HTMLElement | null;
    const unlock = lockBodyScroll();
    ref.current?.focus({ preventScroll: true });
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // A field inside that uses Escape itself (closing its own dropdown or add field) marks it
        // handled, and the dialog stays open: one Escape undoes one thing.
        if (e.defaultPrevented) return;
        e.preventDefault();
        escape.current();
      }
      if (e.key === "Tab") {
        const nodes = Array.from(
          ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || [],
        ).filter((n) => n.getClientRects().length);
        if (!nodes.length) return;
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (
          e.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === ref.current)
        ) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      unlock();
      document.removeEventListener("keydown", key);
      before?.focus({ preventScroll: true });
    };
  }, [active, ref]);
}
